import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Heading, IconButton } from '../../../components/base';
import { Button as Icon } from '../../../components/Button';
import { useAlert } from '../../../context/AlertContext';
import { usePlugin } from '../../../libs/hooks/usePlugin';
import {
  AuthorityType,
  ECreatedStatusType,
  EOpenScopeType,
  EVersionStatusType,
  ExternalSource,
  IPluginPromptData,
} from '../../../libs/models/studio/PluginPromptList';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { showAlertToast } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import { Constants } from '../../../Constants';
import AionuCreateForm from './AIONUCreateForm';
import AionuCreateDialog from '../AIONUCreateDialog';

const LoadingSpinner = () => {
  return (
    <div className="dsx-ModalOverlay">
      <div className="works-loading">
        <span className="loading" aria-hidden="true"></span>
      </div>
    </div>
  );
};

// Create > CreateForm > 여기서 step별로 나뉘어짐
const AionuCreate = () => {
  const plugin = usePlugin();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();
  const [query] = useSearchParams();
  const { user } = useAppSelector((state: RootState) => state.users);
  const [isRegistLoading, setIsRegistLoading] = useState(false);
  const AIONU_IMAGE = `${process.env.REACT_APP_STORAGE_IMAGE_URL}/image/icon_aionu.png`;

  const [formData, setFormData] = useState<IPluginPromptData>({
    partitionKey: Constants.companyCode,
    deleted: false,
    companyCode: Constants.companyCode,
    pluginCategoryId: '',
    name: '',
    description: '',
    // model: '',
    profileUrl: AIONU_IMAGE,
    openScope: EOpenScopeType.Private,
    customPersona: '',
    //ragFileIds: [],
    //ragContentsData: { id: '', contentsDataFileName: '', uploadStatus: 'NotStarted' },
    ragContentsDataId: '', // ragContentDataId가 없으면, 생성이 아예 안됨
    conversationStarters: [],
    bookmarkUserIds: [],
    enabled: true,
    createdMember: user,
    versionStatus: EVersionStatusType.Draft, // 임시저장
    createdStatus: ECreatedStatusType.Registration, // 등록중
    managedMembers: [{ authorityType: AuthorityType.Owner, user: user }],
    externalSource: ExternalSource.Aionu, // aionu 구분
  });
  const [dialogOpen, setDialogOpen] = useState(false); // 초기값을 true로 설정

  useEffect(() => {
    const id = query.get('id');

    if (id) {
      void handleCallDetail(id);
    } else {
      setDialogOpen(true);
    }
  }, []);
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  /**
   * [Func] 상세 조회
   */

  const handleCallDetail = async (id: string) => {
    try {
      const res = await plugin.getPlugin(id);

      setFormData({ ...res });
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * [Func] 임시저장
   * @param type
   */
  const handleDraft = async (isShowAlert?: boolean) => {
    // 임시 저장
    try {
      if (formData.id) {
        const res = await plugin.postPluginDraft({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          createdStatus: formData.createdStatus === 'Complete' ? 'Registration' : formData.createdStatus,
        });
        if (res.statusCode === 200) {
          setFormData((prev) => ({ ...prev, id: formData.id }));
          isShowAlert &&
            dispatch(
              showAlertToast({
                altMessage: '임시저장 되었습니다.',
                altType: AlertType.Info,
              }),
            );
        } else {
          dispatch(
            showAlertToast({
              altMessage: String(res.value.message),
              altType: AlertType.Info,
            }),
          );
        }
      } else {
        const res = await plugin.postPluginDraft({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
        });
        if (res.statusCode === 200) {
          setFormData((prev) => ({
            ...prev,
            id: res.value.id,
          }));
        } else {
          dispatch(
            showAlertToast({
              altMessage: String(res.value.message),
              altType: AlertType.Info,
            }),
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * 배포하기
   */
  const handleRegist = async () => {
    if (!formData.id) {
      const res = await plugin.postPluginDraft({
        ...formData,
        conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
      });
      if (res.statusCode === 200) {
        setFormData((prev) => ({
          ...prev,
          id: res.value.id,
        }));
      }
    }

    console.log('handleRegist  : formData.id', formData.id);
    setIsRegistLoading(true);
    try {
      // CASE.1 임시저장인 상태에서, id 값이 있는경우 (기존 배포 없는 경우)
      if ((formData.versionStatus === 'Draft' || formData.versionStatus === EVersionStatusType.Draft) && formData.id) {
        console.log('handleRegist  : CASE.1');

        // 임시저장된 상태에서 아무것도 변경되지 않고 상태만 등록으로 변경
        await plugin.postPlugin({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          versionStatus: EVersionStatusType.Creat, // 최초
          createdStatus: ECreatedStatusType.Complete,
        });
        setFormData((prev) => ({ ...prev, id: formData.id }));
        setIsRegistLoading(false);
        navigate('/makers/agent');

        // CASE.2 최초 배포된 상태에서, id 값이 있는경우 or CASE.3 업데이트 상태에서, id 값이 있는경우 (기존 배포 버전 있는 경우)
      } else if (
        (formData.versionStatus === 'Creat' ||
          formData.versionStatus === EVersionStatusType.Creat ||
          formData.versionStatus === 'Update' ||
          formData.versionStatus === EVersionStatusType.Update) &&
        formData.id
      ) {
        console.log('handleRegist  : CASE.2');
        await plugin.postPlugin({
          ...formData,
          conversationStarters: formData.conversationStarters?.filter((starter) => starter !== ''),
          versionStatus: EVersionStatusType.Update, // 업데이트버전
          createdStatus: ECreatedStatusType.Complete,
        });
        setFormData((prev) => ({ ...prev, id: formData.id }));
        setIsRegistLoading(false);
        navigate('/makers/agent');
      }
    } catch (error) {
      console.log(error);
    }
    setIsRegistLoading(false);
  };

  /**
   * [Func] 임시저장, 저장
   * @param type
   */
  const handleSave = (type: string, isShowAlert?: boolean) => {
    if (type === 'save') {
      if (validate()) {
        // 배포하기
        showAlert({
          title: '에이전트를 등록하시겠어요?',
          message: (
            <div className="alert-box">
              <strong className="tit">공개 범위</strong>{' '}
              <span className="con">{formData.openScope === EOpenScopeType.Public ? '전체공개' : formData.openScope === EOpenScopeType.Part ? '일부공개': '나만보기'}</span>
            </div>
          ),
          confirmLabel: '배포하기',
          showCancel: true,
          onConfirm: () => void handleRegist(),
        });
      } else {
        dispatch(
          showAlertToast({
            altMessage: '필수 항목을 모두 입력해주세요.',
            altType: AlertType.Warning,
          }),
        );
        return;
      }
    } else {
      void handleDraft(isShowAlert);
    }
  };

  const handleBack = () => {
    showAlert({
      title: '저장하지 않고 페이지를 나가시겠습니까?',
      message: '변경사항이 저장되지 않을 수 있습니다.',
      confirmLabel: '나가기',
      showCancel: true,
      onConfirm: () => {
        navigate('/makers/agent');
      },
    });
  };

  const handleDelete = () => {
    showAlert({
      title: '에이전트를 삭제하시겠습니까?',
      message:
        // <div className="alert-box">
        formData.versionStatus === 'Draft' || formData.versionStatus === EVersionStatusType.Draft
          ? '임시저장된 데이터는 복구 할 수 없습니다.'
          : '삭제 전에 다시 한번 확인해주세요.',
      // {/* </div> */}
      confirmLabel: '삭제하기',
      showCancel: true,
      onConfirm: () => {
        setIsRegistLoading(true);
        plugin
          .deletePlugin(formData)
          .then(() => {
            setIsRegistLoading(false);
            navigate('/makers/agent');
          })
          .catch(() => {
            setIsRegistLoading(false);
          });
      },
    });
  };

  const validate = () => {
    const newErrors = {
      pluginCategoryId: '',
      name: '',
      description: '',
      profileUrl: '',
      model: '',
    };
    if (!formData.profileUrl) newErrors.profileUrl = '이미지를 선택해주세요.';
    if (!formData.pluginCategoryId || formData.pluginCategoryId === 'all')
      newErrors.pluginCategoryId = '카테고리명을 선택해주세요.';
    if (!formData.name) newErrors.name = '에이전트명을 입력해주세요.';
    if (!formData.description) newErrors.description = '소개를 입력해주세요.';
    return Object.values(newErrors).every((value) => value.trim().length === 0);
  };

  return (
    <div className="create-wrapper">
      {isRegistLoading && <LoadingSpinner />}
      <div className="create-header">
        <IconButton name="chevronLeft" size="large" onClick={handleBack}>
          뒤로가기
        </IconButton>
        <Heading as="h1" size="title3" weight="semibold">
          ABC Lab 에이전트 생성
        </Heading>
        <Icon
          className="create-delete"
          control
          icon="delete"
          textBlind={true}
          onClick={() => {
            handleDelete();
          }}
        >
          삭제
        </Icon>
        <div className="h-side">
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              handleSave('temp', true);
            }}
          >
            임시저장
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              handleSave('save');
            }}
          >
            배포하기
          </Button>
        </div>
      </div>
      <AionuCreateDialog
        email={user.email}
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        onSave={() => {
          handleSave('temp');
        }}
        setFormData={setFormData}
      />

      <div className="create-container">
        <div className="create-form">
          <AionuCreateForm formData={formData} setFormData={setFormData} />
        </div>
      </div>
    </div>
  );
};
export default AionuCreate;
